export type ClockAction = 'in' | 'break' | 'resume' | 'out'
export interface ClockEntry {
  client_id: string
  action: ClockAction
  track_location?: boolean
  occurred_at: string
}
export interface ClockState {
  recent: ClockEntry[]
  current: ClockEntry[]
}
export interface PendingPunch extends ClockEntry {
  worker_id: number
  after: string | null
  position: { latitude: number; longitude: number } | null
  accuracy: number | null
}
export const clockLabels: Record<ClockAction, string> = {
  in: 'Clocked in',
  break: 'Started break',
  resume: 'Ended break',
  out: 'Clocked out',
}
/** Clock action label in the worker's language (SOS Field). `clockLabels` stays English for office screens. */
export const clockLabel = (action: ClockAction) =>
  ({
    in: ft('Clocked in'),
    break: ft('Started break'),
    resume: ft('Ended break'),
    out: ft('Clocked out'),
  })[action]
export const clockActions: Record<ClockAction | 'off', ClockAction[]> = {
  off: ['in'],
  in: ['break', 'out'],
  break: ['resume', 'out'],
  resume: ['break', 'out'],
  out: ['in'],
}
export const shortEntry = ({
  action,
  client_id,
  occurred_at,
  track_location,
}: ClockEntry): ClockEntry => ({
  action,
  client_id,
  occurred_at,
  ...(track_location ? { track_location } : {}),
})

// Project unacknowledged events over the last server response. Replayed events are already included.
export function projectClock(
  saved: ClockState,
  pending: PendingPunch[]
): ClockState {
  let current = [...saved.current]
  const recent = [...saved.recent]
  for (const event of pending) {
    if (recent.some((row) => row.client_id === event.client_id)) continue
    if ((recent[0]?.client_id ?? null) !== event.after) break
    recent.unshift(event)
    if (event.action === 'out') current = []
    else if (event.action === 'in') current = [event]
    else current.push(event)
  }
  return { current, recent }
}

export function workingMinutes(current: ClockEntry[], now: number) {
  let total = 0
  current.forEach((item, index) => {
    if (item.action !== 'in' && item.action !== 'resume') return
    const next = current[index + 1]
    total += Math.max(
      0,
      (next ? Date.parse(next.occurred_at) : now) - Date.parse(item.occurred_at)
    )
  })
  return Math.floor(total / 60000)
}
